/**
 * KOAI Chat Message Component
 * 개별 채팅 메시지 표시 + 답변 Office 내보내기
 */

import { Box, Avatar, Paper, Typography, Chip, Collapse, IconButton, Tooltip, Button, Stack } from '@mui/material';
import {
  SmartToy as BotIcon,
  Person as PersonIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Description as DocIcon,
  Slideshow as PptIcon,
  TableChart as ExcelIcon,
  SaveAlt as SaveIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { filesAPI } from '../../services/api';
import { downloadExportedFile, getExportErrorMessage } from '../../utils/exportAnswerDownload';
import ChatChart from './ChatChart';

export default function ChatMessage({ message, onCopy, chartData, questionText, onExportError }) {
  const { t } = useTranslation();
  const [showSources, setShowSources] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [exporting, setExporting] = useState('');
  const isUser = message.role === 'user';
  const canExport = !isUser && !message.isStreaming && !message.isLimitMessage && !!message.content;
  
  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    onCopy?.();
  };

  // 출처 파일 다운로드
  const handleDownloadSource = async (source) => {
    if (!source.fileId) {
      console.warn('No fileId for source:', source);
      return;
    }

    setDownloadingId(source.fileId);
    try {
      const response = await filesAPI.download(source.fileId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', source.source || `file_${source.fileId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleExport = async (format) => {
    if (!canExport || exporting) return;
    setExporting(format);
    try {
      await downloadExportedFile(format, {
        title: t('chat.title'),
        question: questionText || '',
        answer: message.content,
        sources: message.sources || [],
      });
    } catch (err) {
      const msg = await getExportErrorMessage(err, t('chat.exportError'));
      onExportError?.(msg);
    } finally {
      setExporting('');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        mb: 2,
        flexDirection: isUser ? 'row-reverse' : 'row',
      }}
    >
      <Avatar
        sx={{
          bgcolor: isUser ? 'primary.main' : 'secondary.main',
          mx: 1,
          width: 36,
          height: 36
        }}
      >
        {isUser ? <PersonIcon fontSize="small" /> : <BotIcon fontSize="small" />}
      </Avatar>
      
      <Box sx={{ maxWidth: '70%' }}>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            bgcolor: isUser ? 'primary.light' : 'grey.100',
            borderRadius: 2,
            position: 'relative',
            '&:hover .copy-btn': { opacity: 1 }
          }}
        >
          <Typography 
            variant="body1" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {message.content}
          </Typography>
          
          <IconButton
            className="copy-btn"
            size="small"
            onClick={handleCopy}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              opacity: 0,
              transition: 'opacity 0.2s'
            }}
          >
            <CopyIcon fontSize="small" />
          </IconButton>

          {canExport && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}
              >
                <SaveIcon sx={{ fontSize: 16 }} />
                {t('chat.saveAs')}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DocIcon />}
                disabled={!!exporting}
                onClick={() => handleExport('docx')}
              >
                {exporting === 'docx' ? t('chat.exporting') : t('chat.saveWord')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PptIcon />}
                disabled={!!exporting}
                onClick={() => handleExport('pptx')}
              >
                {exporting === 'pptx' ? t('chat.exporting') : t('chat.savePpt')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ExcelIcon />}
                disabled={!!exporting}
                onClick={() => handleExport('xlsx')}
              >
                {exporting === 'xlsx' ? t('chat.exporting') : t('chat.saveExcel')}
              </Button>
            </Stack>
          )}
        </Paper>
        
        {/* Sources */}
        {message.sources && message.sources.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => setShowSources(!showSources)}
            >
              <Typography variant="caption" color="text.secondary">
                출처 {message.sources.length}개
              </Typography>
              {showSources ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
            </Box>
            
            <Collapse in={showSources}>
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {message.sources.map((source, idx) => (
                  <Tooltip 
                    key={idx} 
                    title={source.fileId ? "클릭하여 다운로드" : "파일 정보 없음"}
                    arrow
                  >
                    <Chip
                      label={source.source || `출처 ${idx + 1}`}
                      size="small"
                      variant="outlined"
                      icon={source.fileId ? <DownloadIcon fontSize="small" /> : undefined}
                      onClick={source.fileId ? () => handleDownloadSource(source) : undefined}
                      disabled={downloadingId === source.fileId}
                      sx={{ 
                        fontSize: '0.7rem',
                        cursor: source.fileId ? 'pointer' : 'default',
                        '&:hover': source.fileId ? {
                          bgcolor: 'action.hover',
                          borderColor: 'primary.main'
                        } : {}
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
        
        {/* 디버깅 정보 (개발 환경에서만) */}
        {message.role === 'assistant' && (!message.sources || message.sources.length === 0) && process.env.NODE_ENV === 'development' && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              출처 없음 (sources: {message.sources ? '배열 있음' : '없음'}, length: {message.sources?.length || 0})
            </Typography>
          </Box>
        )}
        
        {/* Metadata */}
        {message.responseTimeMs && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {(message.responseTimeMs / 1000).toFixed(1)}초
          </Typography>
        )}

        {/* Chart */}
        {chartData && !isUser && (
          <ChatChart chartData={chartData} />
        )}
      </Box>
    </Box>
  );
}
